import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { apiGet } from '../../../lib/api';
import { toast } from 'sonner';
import { SectionShell, TextAreaField, MaterialRows, saveSection } from './shared';

export function StoresEditor({ data, dept, deptLabel, onSaved, projectId, date, readOnly }) {
  const [received, setReceived] = useState(() => (data.materials_received || []).map(m => ({ ...m })));
  const [issued, setIssued] = useState(() => (data.materials_issued || []).map(m => ({ ...m })));
  const [notes, setNotes] = useState(data.stores_notes || '');
  const [saving, setSaving] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);

  const clean = (rows, lastKey) => rows
    .filter(r => (r.description || '').trim())
    .map(r => ({ description: r.description.trim(), qty: parseFloat(r.qty) || 0, unit: r.unit, [lastKey]: r[lastKey] || '' }));

  const save = () => saveSection(projectId, date, dept, {
    materials_received: clean(received, 'supplier'),
    materials_issued: clean(issued, 'issued_to'),
    stores_notes: notes,
  }, onSaved, deptLabel, setSaving);

  const loadFromStores = async () => {
    setLoadingStores(true);
    try {
      const summary = await apiGet(`/stores/daily-summary?project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}`);
      const grns = (summary.grns || []).map(g => ({
        description: g.description || g.item_name || '',
        qty: g.qty_received ?? g.qty ?? 0,
        unit: g.unit || 'nos',
        supplier: g.vendor_name || g.supplier || '',
      }));
      const issues = (summary.issues || []).map(i => ({
        description: i.description || i.item_name || '',
        qty: i.qty_issued ?? i.qty ?? 0,
        unit: i.unit || 'nos',
        issued_to: i.issued_to || i.work_order || '',
      }));

      if (grns.length === 0 && issues.length === 0) {
        toast.info(`No stores transactions found for ${date} on this project.`);
        return;
      }

      // Merge: append rows that don't already exist (by description)
      const existingRecvDescs = new Set(received.map(r => (r.description || '').toLowerCase().trim()));
      const newRecv = grns.filter(g => !existingRecvDescs.has((g.description || '').toLowerCase().trim()));

      const existingIssDescs = new Set(issued.map(r => (r.description || '').toLowerCase().trim()));
      const newIss = issues.filter(i => !existingIssDescs.has((i.description || '').toLowerCase().trim()));

      if (newRecv.length > 0) setReceived(prev => [...prev, ...newRecv]);
      if (newIss.length > 0) setIssued(prev => [...prev, ...newIss]);

      const added = newRecv.length + newIss.length;
      if (added > 0) {
        toast.success(`Loaded ${added} row${added > 1 ? 's' : ''} from Stores module.`);
      } else {
        toast.info('All stores transactions are already listed — no new rows to add.');
      }
    } catch {
      toast.error('Could not load stores transactions.');
    } finally {
      setLoadingStores(false);
    }
  };

  return (
    <SectionShell deptLabel={deptLabel} dept={dept} onSave={save} saving={saving} readOnly={readOnly}>
      {/* Load from stores button */}
      <div className="flex justify-end">
        <button
          onClick={loadFromStores}
          disabled={loadingStores}
          data-testid="dpr-stores-load-from-stores"
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-[#3b4872] text-[#3b4872] hover:bg-indigo-50 disabled:opacity-50"
        >
          <Download size={11} />
          {loadingStores ? 'Loading…' : 'Load from Stores module'}
        </button>
      </div>
      <MaterialRows
        label="Materials received today"
        rows={received}
        setRows={setReceived}
        lastCol={{ key: 'supplier', label: 'Supplier' }}
        testid="dpr-stores-received"
      />
      <MaterialRows
        label="Materials issued today"
        rows={issued}
        setRows={setIssued}
        lastCol={{ key: 'issued_to', label: 'Issued to' }}
        testid="dpr-stores-issued"
      />
      <TextAreaField label="Stores notes" value={notes} onChange={setNotes} rows={2} testid="dpr-stores-notes" />
    </SectionShell>
  );
}
